import { z } from "zod";
import { validateCPF, validateCPFOrCNPJ } from "../../../utils/validations";
import { getMaxLength } from "../../../utils/fieldLimits";

const MAX_FILE_SIZE = 5000000; // 5MB
const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

// Regex para validação de CEP (removido - não utilizado)
// const cepRegex = /^\d{5}-?\d{3}$/;

// Enum para estado civil
const EstadoCivil = {
  solteiro: "solteiro",
  casado: "casado",
  divorciado: "divorciado",
  viuvo: "viuvo",
  uniao_estavel: "uniao_estavel",
} as const;

// Função auxiliar para validar datas (aceita tanto Date quanto string)
const validateDate = (value: any) => {
  if (!value) return false;

  let date: Date;
  if (typeof value === 'string') {
    // Se é uma string no formato yyyy-MM-dd, converte diretamente
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      date = new Date(value);
    } else {
      // Tenta parsear como data
      date = new Date(value);
    }
  } else if (value instanceof Date) {
    date = value;
  } else {
    return false;
  }

  if (isNaN(date.getTime())) return false;

  const now = new Date();
  const minDate = new Date("1900-01-01");

  // Ajusta para considerar apenas a data (sem hora)
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const minDateOnly = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());

  return dateOnly <= nowOnly && dateOnly >= minDateOnly;
};

// Schema base para validação do formulário (sem documentos)
const baseClienteFormSchema = z.object({
  // Informações Básicas - Dinâmico baseado no tipo
  nome: z.string()
    .min(1, "Nome é obrigatório")
    .max(getMaxLength('nome') || 255, `Nome deve ter no máximo ${getMaxLength('nome') || 255} caracteres`),
  email: z.string()
    .refine((value) => {
      if (!value || value === "") return true; // Permite vazio
      // Valida email se não for vazio
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }, "Email inválido")
    .refine((value) => {
      if (!value) return true;
      return value.length <= (getMaxLength('email') || 255);
    }, `Email deve ter no máximo ${getMaxLength('email') || 255} caracteres`)
    .optional(),
  tel1: z.string()
    .max(getMaxLength('tel1') || 16, `Telefone deve ter no máximo ${getMaxLength('tel1') || 11} dígitos`)
    .optional(),
  tel2: z.string()
    .max(getMaxLength('tel2') || 16, `Telefone deve ter no máximo ${getMaxLength('tel2') || 11} dígitos`)
    .optional(),

  // Data de nascimento
  dataNascimento: z.union([
    z.date().optional(),
    z.string().optional()
  ]).refine((value) => {
    if (!value) return true;
    return validateDate(value);
  }, {
    message: "Data de nascimento deve estar entre 01/01/1900 e hoje"
  }),

  // Informações da Empresa - Documentos
  razaoSocial: z.string()
    .max(255, "Razão Social deve ter no máximo 255 caracteres")
    .optional(),
  inscricaoEstadual: z.string()
    .max(20, "Inscrição Estadual deve ter no máximo 20 caracteres")
    .optional(),
  cpf: z.string()
    .max(getMaxLength('cpf') || 14, `CPF/CNPJ deve ter no máximo ${getMaxLength('cpf') || 14} dígitos`)
    .refine(
      (value) => {
        if (!value) return true; // Campo opcional
        // Remove formatação e valida
        const cleaned = value.replace(/\D/g, '');

        // Aceita CPF (11 dígitos) ou CNPJ (14 dígitos)
        if (cleaned.length !== 11 && cleaned.length !== 14) {
          return false;
        }

        // Usa a função de validação que detecta automaticamente CPF ou CNPJ
        return validateCPFOrCNPJ(cleaned);
      },
      { message: "CPF/CNPJ inválido" }
    )
    .optional(),
  rg: z.string()
    .max(getMaxLength('rg') || 20, `RG deve ter no máximo ${getMaxLength('rg') || 20} caracteres`)
    .optional(),
  dataEmissaoRg: z.union([
    z.date().optional(),
    z.string().optional()
  ]).refine((value) => {
    if (!value) return true; // Campo opcional
    return validateDate(value);
  }, {
    message: "Data de emissão do RG deve estar entre 01/01/1900 e hoje"
  }),

  // Informações do Responsável (para PJ)
  nomeResponsavel: z.string()
    .max(255, "Nome do Responsável deve ter no máximo 255 caracteres")
    .optional(),
  cpfResponsavel: z.string()
    .max(11, "CPF do Responsável deve ter no máximo 11 dígitos")
    .refine(
      (value) => {
        if (!value) return true; // Campo opcional
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length !== 11) return false;
        return validateCPF(cleaned);
      },
      { message: "CPF do Responsável inválido" }
    )
    .optional(),
  telResponsavel: z.string()
    .max(16, "Telefone do Responsável deve ter no máximo 16 dígitos")
    .optional(),
  emailResponsavel: z.string()
    .refine((value) => {
      if (!value || value === "") return true; // Permite vazio
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }, "Email do Responsável inválido")
    .refine((value) => {
      if (!value) return true;
      return value.length <= 255;
    }, "Email do Responsável deve ter no máximo 255 caracteres")
    .optional(),

  // Informações Pessoais Complementares
  naturalidade: z.string()
    .max(100, "Naturalidade deve ter no máximo 100 caracteres")
    .optional(),
  nacionalidade: z.string()
    .max(100, "Nacionalidade deve ter no máximo 100 caracteres")
    .optional(),
  atividadeDesenvolvida: z.string()
    .max(255, "Atividade desenvolvida deve ter no máximo 255 caracteres")
    .optional(),
  rendimentoTitular: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      if (typeof val === 'string') {
        const cleaned = val.replace(/[^\d,.-]/g, '').replace(',', '.');
        return cleaned ? parseFloat(cleaned) : undefined;
      }
      return typeof val === 'number' ? val : undefined;
    },
    z.number().positive("Rendimento deve ser positivo").optional()
  ),

  // Informações de Endereço
  cep: z.string()
    .max(getMaxLength('cep') || 8, `CEP deve ter no máximo ${getMaxLength('cep') || 8} caracteres`)
    .optional(),
  logradouro: z.string()
    .max(getMaxLength('logradouro') || 255, `Logradouro deve ter no máximo ${getMaxLength('logradouro') || 255} caracteres`)
    .optional(),
  numero: z.string()
    .max(getMaxLength('numero') || 20, `Número deve ter no máximo ${getMaxLength('numero') || 20} caracteres`)
    .optional(),
  complemento: z.string()
    .max(getMaxLength('complemento') || 255, `Complemento deve ter no máximo ${getMaxLength('complemento') || 255} caracteres`)
    .optional(),
  bairro: z.string()
    .max(getMaxLength('bairro') || 100, `Bairro deve ter no máximo ${getMaxLength('bairro') || 100} caracteres`)
    .optional(),
  cidade: z.string()
    .max(getMaxLength('cidade') || 100, `Cidade deve ter no máximo ${getMaxLength('cidade') || 100} caracteres`)
    .optional(),
  estado: z.string()
    .max(getMaxLength('estado') || 2, `Estado deve ter no máximo ${getMaxLength('estado') || 2} caracteres`)
    .optional(),

  // Informações Familiares
  nomeMae: z.string()
    .max(getMaxLength('nomeMae') || 255, `Nome da mãe deve ter no máximo ${getMaxLength('nomeMae') || 255} caracteres`)
    .optional(),
  nomePai: z.string()
    .max(getMaxLength('nomePai') || 255, `Nome do pai deve ter no máximo ${getMaxLength('nomePai') || 255} caracteres`)
    .optional(),
  estadoCivil: z.enum([
    EstadoCivil.solteiro,
    EstadoCivil.casado,
    EstadoCivil.divorciado,
    EstadoCivil.viuvo,
    EstadoCivil.uniao_estavel
  ]).optional(),
  nomeConjuge: z.string()
    .max(getMaxLength('nomeConjuge') || 255, `Nome do cônjuge deve ter no máximo ${getMaxLength('nomeConjuge') || 255} caracteres`)
    .optional(),
  cpfConjuge: z.string()
    .max(getMaxLength('cpfConjuge') || 11, `CPF do cônjuge deve ter no máximo ${getMaxLength('cpfConjuge') || 11} dígitos`)
    .refine(
      (value) => {
        if (!value) return true; // Campo opcional
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length !== 11) return false;
        return validateCPF(cleaned);
      },
      { message: "CPF do cônjuge inválido" }
    )
    .optional(),
  dataNascimentoConjuge: z.union([
    z.date().optional(),
    z.string().optional()
  ]).refine((value) => {
    if (!value) return true; // Campo opcional
    return validateDate(value);
  }, {
    message: "Data de nascimento do cônjuge deve estar entre 01/01/1900 e hoje"
  }),

  // Dados Bancários
  tipoConta: z.enum(["corrente", "poupanca", "salario"]).optional(),
  banco: z.string()
    .max(getMaxLength('banco') || 100, `Banco deve ter no máximo ${getMaxLength('banco') || 100} caracteres`)
    .optional(),
  agencia: z.string()
    .max(getMaxLength('agencia') || 20, `Agência deve ter no máximo ${getMaxLength('agencia') || 20} caracteres`)
    .optional(),
  conta: z.string()
    .max(getMaxLength('conta') || 20, `Conta deve ter no máximo ${getMaxLength('conta') || 20} caracteres`)
    .optional(),

  // Campos de Qualificação (obrigatórios para relacionadores)
  temEntrada: z.boolean().default(false),
  tipoBem: z.enum(["IMOVEL", "VEICULO", "OUTROS"], {
    required_error: "Tipo do bem é obrigatório",
  }),
  valorEntrada: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      if (typeof val === 'string') {
        const cleaned = val.replace(/[^\d,.-]/g, '').replace(',', '.');
        return cleaned ? parseFloat(cleaned) : undefined;
      }
      return typeof val === 'number' ? val : undefined;
    },
    z.number().positive("Valor da entrada deve ser positivo").optional()
  ),
  valorParcelaOrcamento: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      if (typeof val === 'string') {
        const cleaned = val.replace(/[^\d,.-]/g, '').replace(',', '.');
        return cleaned ? parseFloat(cleaned) : undefined;
      }
      return typeof val === 'number' ? val : undefined;
    },
    z.number({ required_error: "Defina o valor das parcelas" }).positive("Valor da parcela deve ser positivo")
  ),
  tempoProcura: z.enum(['menos_de_1_mes', '1_a_3_meses', '3_a_6_meses', 'mais_de_6_meses'], {
    required_error: "Tempo de procura é obrigatório",
    invalid_type_error: "Tempo de procura é obrigatório",
  }),
  prioridade: z.enum(['ALTA', 'MEDIA', 'BAIXA', 'APENAS_PESQUISANDO'], {
    required_error: "Prioridade é obrigatória",
    invalid_type_error: "Prioridade é obrigatória",
  }),
  tempoAluguel: z.string()
    .max(50, "Tempo de aluguel deve ter no máximo 50 caracteres")
    .optional(),

  // Status Atual do Cliente (opcional)
  statusAtual: z.string()
    .max(1000, "Status atual deve ter no máximo 1000 caracteres")
    .optional(),
});

// Schema para validação de documentos
const documentosSchema = z.object({
  documentoComFotoDoc: z
    .any()
    .refine((files) => {
      if (!(files instanceof FileList)) return true;
      return files.length === 0 || files.length > 0;
    }, "Documento com foto é obrigatório")
    .refine((files) => {
      if (!(files instanceof FileList)) return true;
      return files.length === 0 || files[0]?.size <= MAX_FILE_SIZE;
    }, "Tamanho máximo: 5MB")
    .refine(
      (files) => {
        if (!(files instanceof FileList)) return true;
        return files.length === 0 || ACCEPTED_FILE_TYPES.includes(files[0]?.type);
      },
      "Formato aceito: .jpg, .jpeg, .png e .pdf"
    ),
  residenciaDoc: z
    .any()
    .refine((files) => {
      if (!(files instanceof FileList)) return true;
      return files.length === 0 || files.length > 0;
    }, "Comprovante de residência é obrigatório")
    .refine((files) => {
      if (!(files instanceof FileList)) return true;
      return files.length === 0 || files[0]?.size <= MAX_FILE_SIZE;
    }, "Tamanho máximo: 5MB")
    .refine(
      (files) => {
        if (!(files instanceof FileList)) return true;
        return files.length === 0 || ACCEPTED_FILE_TYPES.includes(files[0]?.type);
      },
      "Formato aceito: .jpg, .jpeg, .png e .pdf"
    ),
  comprovantePagamentoDoc: z
    .any()
    .refine((files) => {
      if (!(files instanceof FileList)) return true;
      return files.length === 0 || files.length > 0;
    }, "Comprovante de pagamento é obrigatório")
    .refine((files) => {
      if (!(files instanceof FileList)) return true;
      return files.length === 0 || files[0]?.size <= MAX_FILE_SIZE;
    }, "Tamanho máximo: 5MB")
    .refine(
      (files) => {
        if (!(files instanceof FileList)) return true;
        return files.length === 0 || ACCEPTED_FILE_TYPES.includes(files[0]?.type);
      },
      "Formato aceito: .jpg, .jpeg, .png e .pdf"
    ),
  declaracoesDoc: z
    .any()
    .refine((files) => {
      if (!(files instanceof FileList)) return true;
      return files.length === 0 || files.length > 0;
    }, "Declarações são obrigatórias")
    .refine((files) => {
      if (!(files instanceof FileList)) return true;
      return files.length === 0 || files[0]?.size <= MAX_FILE_SIZE;
    }, "Tamanho máximo: 5MB")
    .refine(
      (files) => {
        if (!(files instanceof FileList)) return true;
        return files.length === 0 || ACCEPTED_FILE_TYPES.includes(files[0]?.type);
      },
      "Formato aceito: .jpg, .jpeg, .png e .pdf"
    ),
  comprovanteRendaDoc: z
    .any()
    .refine((files) => {
      if (!(files instanceof FileList)) return true;
      return files.length === 0 || files.length > 0;
    }, "Comprovante de renda é obrigatório")
    .refine((files) => {
      if (!(files instanceof FileList)) return true;
      return files.length === 0 || files[0]?.size <= MAX_FILE_SIZE;
    }, "Tamanho máximo: 5MB")
    .refine(
      (files) => {
        if (!(files instanceof FileList)) return true;
        return files.length === 0 || ACCEPTED_FILE_TYPES.includes(files[0]?.type);
      },
      "Formato aceito: .jpg, .jpeg, .png e .pdf"
    ),
});

// Função para criar schema dinâmico baseado no tipo de cliente
const createDynamicSchema = (_clienteType: 'PF' | 'PJ' | null) => {
  // Por enquanto, retorna o schema base - validação específica será feita no componente
  return baseClienteFormSchema;
};

// Schema para validação do formulário sem documentos obrigatórios
export const clienteFormSchema = baseClienteFormSchema
  .extend({
    documentoComFotoDoc: documentosSchema.shape.documentoComFotoDoc.optional(),
    residenciaDoc: documentosSchema.shape.residenciaDoc.optional(),
    comprovantePagamentoDoc: documentosSchema.shape.comprovantePagamentoDoc.optional(),
    declaracoesDoc: documentosSchema.shape.declaracoesDoc.optional(),
    comprovanteRendaDoc: documentosSchema.shape.comprovanteRendaDoc.optional(),
  })
  .refine(
    (data) => {
      // Se tem entrada, valor da entrada é obrigatório
      if (data.temEntrada === true) {
        return data.valorEntrada !== undefined && data.valorEntrada !== null && data.valorEntrada > 0;
      }
      return true;
    },
    {
      message: "Valor da entrada é obrigatório quando o cliente tem entrada",
      path: ["valorEntrada"],
    }
  );

// Schema do modal enxuto de criação/edição de cliente no fluxo de lead (só identificação)
export const clienteBasicoSchema = baseClienteFormSchema
  .pick({ nome: true, email: true, cpf: true, tel2: true })
  .extend({
    tel1: z.string({ required_error: "Telefone é obrigatório" })
      .trim()
      .min(1, "Telefone é obrigatório")
      .max(getMaxLength('tel1') || 16, `Telefone deve ter no máximo ${getMaxLength('tel1') || 11} dígitos`),
  });

export type ClienteBasicoValues = z.infer<typeof clienteBasicoSchema>;

// Schema para validação do formulário com documentos obrigatórios
export const clienteFormSchemaWithDocs = baseClienteFormSchema.merge(documentosSchema);

// Função para obter schema dinâmico
export const getDynamicClienteFormSchema = (clienteType: 'PF' | 'PJ' | null) => {
  return createDynamicSchema(clienteType).merge(documentosSchema);
};

export type ClienteFormValues = z.infer<typeof clienteFormSchemaWithDocs>;

// Exportar tipos úteis
export type EstadoCivilType = keyof typeof EstadoCivil;
export type TipoContaType = "corrente" | "poupanca" | "salario";
